import { TableContainer, Paper, TableBody, TableHead, TableRow, Theme, useMediaQuery } from "@mui/material"
import TextCell from "../components/TextCell"
import { SettingTable, TableCellButton, DefaultButton } from "../Settings"
import { useState } from "react";
import SliderRow from "../components/SliderRow";
import { ISliderInterface } from "../components/SliderCell";
import ConfirmationDialog from "../../../components/Misc/Dialog/ConfirmationDialog";

interface IDetectionProps {
    findDefaultFVSliderInterface: (name: string) => ISliderInterface | undefined,
    findFVSliderInterface: (name: string) => (ISliderInterface | undefined),
    setFVSliderInterfaceOption: (op: ISliderInterface, basic?: boolean | undefined) => void
    resetValueToDefault: (name: string) => void,
}

const Detection: React.FC<IDetectionProps> = (props) => {

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [openDialog, setOpenDialog] = useState<boolean>(false)

    const getInterOcularTuple: [ISliderInterface | undefined, ISliderInterface | undefined] = [
        props.findFVSliderInterface("MinimalInterOcularDistance"),
        props.findFVSliderInterface("MaximalInterOcularDistance")
    ];

    const getDefaultInterOcularTuple: [ISliderInterface | undefined, ISliderInterface | undefined] = [
        props.findDefaultFVSliderInterface("MinimalInterOcularDistance"),
        props.findDefaultFVSliderInterface("MaximalInterOcularDistance")
    ];

    const handleResetClick = () => {
        props.resetValueToDefault("DetectionConfidenceThreshold")
        props.resetValueToDefault("MinimalInterOcularDistance")
        props.resetValueToDefault("MaximalInterOcularDistance")
        props.resetValueToDefault("DetectionMaximalRoll")
        props.resetValueToDefault("DetectionMaximalYaw")
        props.resetValueToDefault("DetectionMaximalPitch")
    }

    return (
        <>
        <TableContainer component={Paper} style={{marginBottom: "2rem", tableLayout: "fixed"}}>
            <SettingTable>
                <TableHead>
                    <TableRow>
                        <TextCell head title="Detection" subtitle="" moreInfo={
                            <>
                                The MegaMatcher ID App utilizes a hybrid <strong>Face Detection + Face Tracking</strong> pipeline to ensure robust, real-time stream processing with ICAO compliance.
                                <ol>
                                    <li>
                                        Initial Face Detection
                                        Face acquisition begins with the Face Detection model, which operates using its own configurable parameters (e.g., confidence thresholds, image scale, performance settings).
                                        Once a suitable face is detected and validated according to the defined criteria, the system transitions to tracking mode.
                                    </li>
                                    <li>
                                        Face Tracking Phase
                                        After successful detection, the Face Tracking model takes control. The tracker continuously monitors even minor facial movements between frames.
                                        This design provides two major benefits:
                                        <ul>
                                            <li>
                                                <strong>Significant performance improvement</strong>
                                            </li>
                                            <li>
                                                <strong>Reduced CPU utilization,</strong> as full face detection is no longer required for every frame
                                            </li>
                                        </ul>
                                        The tracker maintains temporal continuity and ensures stable face positioning for ICAO checks and further biometric processing.
                                    </li>
                                    <li>
                                        Automatic Fallback to Detection
                                    </li>
                                    If any of the following conditions occur:
                                    <ul>
                                        <li>The face becomes occluded</li>
                                        <li>The face moves excessively fast</li>
                                        <li>The face leaves the frame entirely</li>
                                        <li>Tracking confidence drops below the configured threshold</li>
                                    </ul>
                                    The tracking process is automatically terminated. Control is then returned to the <strong>Face Detection model,</strong> which resumes full-frame scanning until a valid face is detected again.
                                    <li>
                                        Behavior Configuration
                                    </li>
                                    Depending on configured parameters, the system can be set to:
                                    <ul>
                                        <li>
                                            <strong>Reset the workflow</strong> and restart processing when the face leaves the frame, or
                                        </li>
                                        <li>
                                            <strong>Remain in standby mode,</strong> continuously waiting until a face reappears
                                        </li>
                                    </ul>
                                    This configurable behavior allows the application to adapt to different operational scenarios (e.g., kiosk mode, mobile capture, unattended enrollment).
                                </ol>
                            </>
                        }
                        />
                        <TableCellButton align="right" colSpan={isMobile? 2 : 3}>
                            <DefaultButton variant="text" color="primary" onClick={() => setOpenDialog(true)}>
                                Reset to default
                            </DefaultButton>
                        </TableCellButton>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Detection confidence"
                        subtitle="Determines if the detection of a face is confident enough."
                        title="Threshold range"
                        option={props.findFVSliderInterface("DetectionConfidenceThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("DetectionConfidenceThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="raising"
                        active
                        lockMaxValue
                        dontShowWarningMsg
                    />
                    <SliderRow<[ISliderInterface | undefined, ISliderInterface | undefined]>
                        mobile={isMobile}
                        textTitle="Inter ocular distance"
                        subtitle="Determines value of inter ocular distance."
                        title="Distance"
                        option={getInterOcularTuple}
                        defaultOption={getDefaultInterOcularTuple}
                        setOption={(options, basic) => {
                            if (options[0]) {
                                props.setFVSliderInterfaceOption(options[0], basic);
                            }
                            if (options[1]) {
                                props.setFVSliderInterfaceOption(options[1], basic);
                            }
                        }}
                        minValue={32}
                        maxValue={4000}
                        type="range"
                        active
                        lockMaxValue
                        valueLabel={(x) => x.toString()}
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Maximal roll"
                        subtitle="Determines if to reject faces that have higher roll value."
                        title="Angle"
                        option={props.findFVSliderInterface("DetectionMaximalRoll")}
                        defaultOption={props.findDefaultFVSliderInterface("DetectionMaximalRoll")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={180}
                        type="falling"
                        active
                        lockMaxValue
                        dontShowWarningMsg
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Maximal yaw"
                        subtitle="Determines if to reject faces that have higher yaw value."
                        title="Angle"
                        option={props.findFVSliderInterface("DetectionMaximalYaw")}
                        defaultOption={props.findDefaultFVSliderInterface("DetectionMaximalYaw")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={90}
                        type="falling"
                        active
                        lockMaxValue
                        dontShowWarningMsg
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Maximal pitch"
                        subtitle="Determines if to reject faces that have higher pitch value."
                        title="Angle"
                        option={props.findFVSliderInterface("DetectionMaximalPitch")}
                        defaultOption={props.findDefaultFVSliderInterface("DetectionMaximalPitch")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={90}
                        type="falling"
                        active
                        lockMaxValue
                        dontShowWarningMsg
                    />
                </TableBody>
            </SettingTable>
        </TableContainer>
        <ConfirmationDialog
            open={openDialog}
            setOpen={setOpenDialog}
            title="Reset detection settings"
            text="Reset detection settings to default values?"
            cancelText="Cancel"
            confirmText="Yes"
            confirm={handleResetClick}
            confirmColor="error"
        />
        </>
    )
}

export default Detection